import CloseIcon from '@mui/icons-material/Close';
import type { Breakpoint, PaperProps as PaperPropsType } from '@mui/material';
import { Dialog, DialogTitle, IconButton } from '@mui/material';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { IS_DEV } from 'env';
import type { ReactNode } from 'react';
import { cloneElement, isValidElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
    children?: ReactNode;
    open?: boolean;
    onClose: () => void;
    isSmallHeight?: boolean;
    maxWidth?: Breakpoint;
    additionalButtons?: ReactNode[];
    leftActionGroup?: ReactNode;
    showCloseButton?: boolean;
    showCloseIcon?: boolean;
    disableClosing?: boolean;
    dialogTitle?: ReactNode;
    PaperProps?: Partial<PaperPropsType>;
    className?: string;
    fullHeight?: boolean;
};

/** @deprecated Use <GeneralModal> or <ConfirmationModal> or <InfoModal>*/
const DeprecatedSimpleModal = ({
    open = true,
    onClose: onCloseProp,
    isSmallHeight = undefined,
    children = null,
    maxWidth = 'sm',
    additionalButtons = [],
    leftActionGroup = [],
    showCloseButton = true,
    showCloseIcon = false,
    disableClosing = false,
    dialogTitle = undefined,
    PaperProps = {},
    className = undefined,
    fullHeight = false,
}: Props) => {
    const { t } = useTranslation();

    // If disableClosing is true:
    // We block MUI native closing feature on click outside modal and escape key.
    const onClose = useCallback(
        (
            event: React.SyntheticEvent<Element, Event>,
            reason: 'escapeKeyDown' | 'backdropClick',
        ) => {
            if (disableClosing && event) {
                if (['backdropClick', 'escapeKeyDown'].includes(reason)) {
                    return;
                }
            }
            onCloseProp();
        },
        [disableClosing],
    );
    const displayCloseButton =
        (!showCloseButton && !disableClosing) || showCloseIcon;

    // Ensure each additional button has a unique React key to prevent key warnings
    const additionalButtonsWithReactKey = additionalButtons.map(
        (button, index) => {
            if (isValidElement(button) && !button.key) {
                if (IS_DEV) {
                    // eslint-disable-next-line no-console
                    console.trace(
                        'Please provide a unique key to the buttons you are passing to DeprecatedSimpleModal',
                    );
                }
                const newKey = `additional-button-${index}`;
                return cloneElement(button, {
                    key: newKey,
                });
            }
            return button;
        },
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            classes={{ paper: isSmallHeight ? 'paper-static-size' : '' }}
            fullWidth
            maxWidth={maxWidth}
            className={className}
            slotProps={{ paper: PaperProps }}
        >
            {dialogTitle && (
                <DialogTitle variant="h4" color="primary">
                    {dialogTitle}
                </DialogTitle>
            )}
            {children && (
                <DialogContent sx={fullHeight ? { height: '100%' } : {}}>
                    {children}
                </DialogContent>
            )}
            {displayCloseButton && (
                <DialogActions sx={{ position: 'absolute', top: 0, right: 0 }}>
                    <IconButton aria-label="close" onClick={onCloseProp}>
                        <CloseIcon />
                    </IconButton>
                </DialogActions>
            )}
            <DialogActions sx={{ justifyContent: 'space-between' }}>
                <div>{leftActionGroup}</div>
                <div>
                    {showCloseButton && !disableClosing && (
                        <Button
                            data-testid="e2e-simple-modal-close-button"
                            onClick={onCloseProp}
                        >
                            {t('common.close')}
                        </Button>
                    )}
                    {additionalButtonsWithReactKey}
                </div>
            </DialogActions>
        </Dialog>
    );
};

export default DeprecatedSimpleModal;
